import {
  DateRangePickerField,
  Form,
  NumericFormatField,
  type OperationType,
  SelectField,
  SubmitButton,
  TextInputField,
} from '@/components';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  AccountingTypeFormSchema,
  AccountingTypeLabelModifier,
  accountingType as accountingTypeModel,
} from '../../models';
import { accountTypeCodeLength } from '../../utils';
import { getCodeMaxValue } from '../../utils/get-code-max-value';

type FormValues = z.infer<typeof AccountingTypeFormSchema>;

type Props = {
  type: OperationType;
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const AccountingTypeForm: FC<Props> = ({
  type,
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
    watch,
    setValue,
    resetField,
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(AccountingTypeFormSchema),
  });

  const accountingType = watch('type');

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <NumericFormatField
        name="code"
        label={t('code')}
        control={control}
        disableSeparator
        max={getCodeMaxValue(accountTypeCodeLength())}
        errors={errors}
        required
      />
      <SelectField
        name="type"
        label={t('accounting-type')}
        control={control}
        options={accountingTypeModel.options.map((value) => ({
          value,
          label: AccountingTypeLabelModifier[value],
        }))}
        errors={errors}
        shouldCoexistOnChange
        onChange={(value) => {
          if (value === 'general') {
            setValue('name', t('general-account'));
          } else {
            resetField('name', { defaultValue: null as any });
          }
        }}
        required
        disabled={type === 'edit'}
      />
      <TextInputField
        name="name"
        autocompleteKey="accountingType.name"
        label={t('accounting-name')}
        control={control}
        errors={errors}
        required
        disabled={accountingType === 'general'}
      />
      <DateRangePickerField
        name="period"
        label={t('start-and-end-year')}
        control={control}
        errors={errors}
        picker="year"
        allowEmpty={[true, true]}
      />
      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
